import type { Material, Recipe } from '../../types/progression'

/*
 * Jewel packs (Lahap) no se incluyen: los nombres de los paquetes y su NPC no están
 * identificados en el catálogo; quedan registrados como pendiente en el informe del staff.
 */

/*
 * Recetas de la Chaos Machine. Base editorial: referencia clásica de Season 6.
 * No incluye porcentajes de éxito: se publicarán solo con la fórmula confirmada.
 */

// IDs comprobados en el catálogo de Vanguard: Bless 14,13 · Soul 14,14 · Creation 14,22 · Chaos 12,15.
const JOC = { gameName: 'Jewel of Chaos', itemId: '12,15', esName: 'Joya del Caos' }
const BLESS = { gameName: 'Jewel of Bless', itemId: '14,13', esName: 'Joya de Bendición' }
const SOUL = { gameName: 'Jewel of Soul', itemId: '14,14', esName: 'Joya del Alma' }
const CREATION = { gameName: 'Jewel of Creation', itemId: '14,22', esName: 'Joya de la Creación' }
const LIFE = { gameName: 'Jewel of Life', esName: 'Joya de la Vida' }

const joc: Material = { id: 'joc', ...JOC, qty: '1', qtyStatus: 'clasica' }
const creation: Material = { id: 'creation', ...CREATION, qty: '1', qtyStatus: 'clasica' }
const soulPack: Material = { id: 'soul-pack', ...SOUL, qty: '1 paquete de 10', qtyStatus: 'clasica' }
const blessPack: Material = { id: 'bless-pack', ...BLESS, qty: '1 paquete de 10', qtyStatus: 'clasica' }
const n = (id: string, base: Omit<Material, 'id' | 'qty' | 'qtyStatus'>, qty: string): Material => ({ id, ...base, qty, qtyStatus: 'clasica' })

const ZEN_PENDING = 'Pendiente de confirmar para Vanguard.'
const FAIL_PENDING = 'Pendiente de confirmar: no se asume que todos los ingredientes se pierdan.'

export const recipes: Recipe[] = [
  {
    id: 'arma-chaos',
    title: 'Crear un arma Chaos',
    shortTitle: 'Arma Chaos',
    aliases: ['arma chaos', 'chaos weapon', 'chaos dragon axe', 'chaos lightning staff', 'chaos nature bow', 'arma caos'],
    summary: 'Primer paso para las alas: combina equipo apto con una Jewel of Chaos.',
    npc: 'chaos-goblin',
    ingredients: [
      { id: 'equipo', esName: 'Equipo apto para la combinación', qty: '1', qtyStatus: 'clasica', requirement: '+4 con opción adicional +4 como mínimo', requirementStatus: 'clasica' },
      joc,
    ],
    results: [
      { itemId: '2,6', gameName: 'Chaos Dragon Axe' },
      { itemId: '5,7', gameName: 'Chaos Lighting Staff', altNames: ['Chaos Lightning Staff'] },
      { itemId: '4,6', gameName: 'Chaos Nature Bow' },
    ],
    zen: ZEN_PENDING,
    zenStatus: 'pendiente',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
    next: 'alas-evo-1',
  },
  {
    id: 'alas-evo-1',
    title: 'Crear alas Evo 1',
    shortTitle: 'Alas Evo 1',
    aliases: ['alas evo 1', 'alas nivel 1', 'primeras alas', 'wings 1', 'alas de primera generacion', 'alas 1', 'wings evo 1'],
    summary: 'Alas de primera generación a partir de un arma Chaos.',
    npc: 'chaos-goblin',
    ingredients: [
      { id: 'arma-chaos', esName: 'Arma Chaos', qty: '1', qtyStatus: 'clasica', requirement: '+4 con opción adicional +4 como mínimo', requirementStatus: 'clasica', recipeId: 'arma-chaos' },
      joc,
    ],
    results: [
      { gameName: 'Wings of Satan', who: 'Dark Knight' },
      { gameName: 'Wings of Heaven', who: 'Dark Wizard' },
      { gameName: 'Wings of Elf', who: 'Fairy Elf' },
      { itemId: '12,41', gameName: 'Wing of Curse', altNames: ['Wings of Curse'], who: 'Summoner' },
    ],
    resultNote: 'El resultado depende de la configuración de la mezcla. No se garantiza que puedas elegir el tipo de alas.',
    zen: ZEN_PENDING,
    zenStatus: 'pendiente',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
    prev: 'arma-chaos',
    next: 'alas-evo-2',
  },
  {
    id: 'alas-evo-2',
    title: 'Crear alas Evo 2',
    shortTitle: 'Alas Evo 2',
    aliases: ['alas evo 2', 'alas nivel 2', 'segundas alas', 'wings 2', 'alas de segunda generacion', 'alas 2', 'wings evo 2'],
    summary: 'Alas de segunda generación con Loch’s Feather.',
    npc: 'chaos-goblin',
    ingredients: [
      { id: 'alas-1', esName: 'Alas Evo 1', qty: '1', qtyStatus: 'clasica', requirement: 'Mínimos exigidos a las alas pendientes de confirmar', requirementStatus: 'pendiente', recipeId: 'alas-evo-1' },
      { id: 'loch', itemId: '13,14', gameName: "Loch's Feather", altNames: ['Loch’s Feather', 'Lochs Feather'], esName: 'Pluma de Loch', qty: '1', qtyStatus: 'clasica' },
      joc,
    ],
    results: [
      { gameName: 'Wings of Dragon', who: 'Blade Knight' },
      { gameName: 'Wings of Soul', who: 'Soul Master' },
      { gameName: 'Wings of Spirits', who: 'Muse Elf' },
      { itemId: '12,42', gameName: 'Wind of Despair', altNames: ['Wings of Despair'], who: 'Bloody Summoner' },
      { gameName: 'Wings of Darkness', who: 'Magic Gladiator' },
    ],
    resultNote: 'Usar equipo Excellent como material adicional para mejorar la mezcla solo se publicará cuando esté confirmado.',
    zen: ZEN_PENDING,
    zenStatus: 'pendiente',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
    prev: 'alas-evo-1',
    next: 'pluma-condor',
  },
  {
    id: 'capas',
    title: 'Crear capas',
    shortTitle: 'Capas',
    aliases: ['capa', 'capas', 'cape', 'capes', 'capa dark lord', 'capa rage fighter', 'cape of lord', 'cape of fighter', 'crest of monarch'],
    summary: 'Capas de Dark Lord y Rage Fighter. Cada clase tiene su propia receta.',
    npc: 'chaos-goblin',
    classes: 'Dark Lord y Rage Fighter',
    ingredients: [],
    results: [],
    zen: ZEN_PENDING,
    zenStatus: 'pendiente',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
    prev: 'alas-evo-1',
    variants: [
      {
        id: 'dl',
        title: 'Capa de Dark Lord',
        who: 'Dark Lord',
        ingredients: [
          { id: 'alas-1', esName: 'Alas Evo 1', qty: '1', qtyStatus: 'clasica', recipeId: 'alas-evo-1' },
          { id: 'crest', gameName: 'Crest of Monarch', esName: 'Emblema del Monarca', qty: '1', qtyStatus: 'clasica' },
          joc,
        ],
        results: [{ gameName: 'Cape of Lord' }],
        note: 'Referencia clásica: usa Crest of Monarch en lugar de Loch’s Feather.',
      },
      {
        id: 'rf',
        title: 'Capa de Rage Fighter',
        who: 'Rage Fighter',
        ingredients: [
          { id: 'alas-1', esName: 'Alas Evo 1', qty: '1', qtyStatus: 'pendiente', recipeId: 'alas-evo-1' },
          { id: 'crest', gameName: 'Crest of Monarch', esName: 'Emblema del Monarca', qty: '1', qtyStatus: 'pendiente' },
          joc,
        ],
        results: [{ itemId: '12,49', gameName: 'Cloak of Warrior', altNames: ['Cape of Fighter'], who: 'Rage Fighter' }],
        note: 'La capa del Rage Fighter en Vanguard es Cloak of Warrior (12,49), compatible con Rage Fighter según la configuración. Sus ingredientes siguen pendientes: no se asume que sea la misma receta que la del Dark Lord.',
      },
    ],
  },
  {
    id: 'pluma-condor',
    title: 'Crear Condor Feather',
    shortTitle: 'Condor Feather',
    aliases: ['condor feather', 'pluma de condor', 'pluma condor', 'feather of condor'],
    summary: 'Material para las alas Evo 3, fabricado a partir de alas Evo 2.',
    npc: 'chaos-goblin',
    ingredients: [
      { id: 'alas-2', esName: 'Alas Evo 2', qty: '1', qtyStatus: 'clasica', requirement: '+9 con opción adicional +4 como mínimo', requirementStatus: 'clasica', recipeId: 'alas-evo-2' },
      { id: 'ancient', esName: 'Ítem Ancient', qty: '1', qtyStatus: 'clasica', requirement: '+7 con opción adicional +4 como mínimo', requirementStatus: 'clasica' },
      joc,
      creation,
      soulPack,
    ],
    results: [{ itemId: '13,53', gameName: 'Condor Feather', altNames: ['Feather of Condor'] }],
    zen: ZEN_PENDING,
    zenStatus: 'pendiente',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
    prev: 'alas-evo-2',
    next: 'alas-evo-3',
  },
  {
    id: 'alas-evo-3',
    title: 'Crear alas Evo 3',
    shortTitle: 'Alas Evo 3',
    aliases: ['alas evo 3', 'alas nivel 3', 'terceras alas', 'wings 3', 'alas de tercera generacion', 'alas 3', 'wings evo 3'],
    summary: 'Alas de tercera generación con Condor Feather y Condor Flame.',
    npc: 'chaos-goblin',
    ingredients: [
      { id: 'condor-feather', itemId: '13,53', gameName: 'Condor Feather', altNames: ['Feather of Condor'], esName: 'Pluma de Cóndor', qty: '1', qtyStatus: 'clasica', recipeId: 'pluma-condor' },
      { id: 'condor-flame', itemId: '13,52', gameName: 'Condor Flame', altNames: ['Flame of Condor'], esName: 'Llama de Cóndor', qty: '1', qtyStatus: 'clasica', note: 'La referencia clásica la asocia a Barracks; la ubicación de Vanguard sale de sus rutas de obtención.', classic: { map: 'Barracks' } },
      { id: 'excellent', esName: 'Ítem Excellent', qty: '1', qtyStatus: 'clasica', requirement: '+9 con opción adicional +4 como mínimo', requirementStatus: 'clasica' },
      joc,
      creation,
      blessPack,
      soulPack,
    ],
    results: [
      { itemId: '12,36', gameName: 'Wing of Storm', altNames: ['Wings of Storm'], who: 'Blade Master' },
      { itemId: '12,37', gameName: 'Wing of Eternal', altNames: ['Wings of Eternal'], who: 'Grand Master' },
      { itemId: '12,38', gameName: 'Wing of Illusion', altNames: ['Wings of Illusion'], who: 'High Elf' },
      { itemId: '12,43', gameName: 'Wing of Dimension', altNames: ['Wings of Dimension'], who: 'Dimension Master' },
      { itemId: '12,39', gameName: 'Wing of Ruin', altNames: ['Wings of Ruin'], who: 'Duel Master' },
      // Capas de tercera evolución: existencia y clases en 01_items y 05_item_classes; la receta es referencia clásica.
      { itemId: '12,40', gameName: 'Cape of Emperor', who: 'Lord Emperor (Dark Lord, tercera evolución)' },
      { itemId: '12,50', gameName: 'Lord of the Cape', altNames: ['Cape of Overrule'], who: 'Fist Master (Rage Fighter, tercera evolución)' },
    ],
    zen: ZEN_PENDING,
    zenStatus: 'pendiente',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
    prev: 'pluma-condor',
  },

  // ── Mascotas y monturas ───────────────────────────────────────────────
  {
    id: 'dark-horse',
    group: 'mascotas',
    machine: 'Trainer',
    title: 'Crear Dark Horse',
    shortTitle: 'Dark Horse',
    aliases: ['dark horse', 'caballo', 'caballo oscuro', 'caballo del dl', 'caballo dark lord', 'crear caballo', 'como crear dark horse', 'resucitar caballo', 'spirit of dark horse'],
    summary: 'Montura del Dark Lord: se resucita su espíritu con el Trainer.',
    npc: 'pet-trainer',
    classes: 'Dark Lord',
    source: 'Guía del Trainer (referencia clásica Season 6)',
    ingredients: [
      n('spirit-horse', { itemId: '13,31@0', gameName: 'Spirit of Dark Horse', esName: 'Espíritu del caballo oscuro' }, '1'),
      n('bless', BLESS, '5'),
      n('soul', SOUL, '5'),
      n('creation', CREATION, '1'),
      n('joc', JOC, '1'),
    ],
    results: [{ itemId: '13,4', gameName: 'Dark Horse', who: 'Dark Lord' }],
    zen: '5.000.000 Zen (referencia clásica)',
    zenStatus: 'clasica',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
    steps: [
      'Reúne el Spirit of Dark Horse y las joyas de la receta.',
      'Habla con el Trainer.',
      'Elige la opción de resucitar espíritus (“Resurrect Spirit” o su traducción en el cliente).',
      'Coloca los materiales de la receta en la ventana.',
      'Revisa el coste y la probabilidad que muestra la ventana antes de combinar.',
      'Si la combinación tiene éxito, recibirás el Dark Horse.',
    ],
    next: 'dark-raven',
  },
  {
    id: 'dark-raven',
    group: 'mascotas',
    machine: 'Trainer',
    title: 'Crear Dark Raven',
    shortTitle: 'Dark Raven',
    aliases: ['dark raven', 'cuervo', 'cuervo oscuro', 'cuervo del dl', 'crear cuervo', 'como crear dark raven', 'resucitar cuervo', 'spirit of dark raven'],
    summary: 'Mascota del Dark Lord: se resucita su espíritu con el Trainer.',
    npc: 'pet-trainer',
    classes: 'Dark Lord',
    source: 'Guía del Trainer (referencia clásica Season 6)',
    ingredients: [
      n('spirit-raven', { itemId: '13,31@1', gameName: 'Spirit of Dark Raven', esName: 'Espíritu del cuervo oscuro' }, '1'),
      n('bless', BLESS, '2'),
      n('soul', SOUL, '2'),
      n('creation', CREATION, '1'),
      n('joc', JOC, '1'),
    ],
    results: [{ itemId: '13,5', gameName: 'Dark Raven', who: 'Dark Lord' }],
    zen: '1.000.000 Zen (referencia clásica)',
    zenStatus: 'clasica',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
    steps: [
      'Reúne el Spirit of Dark Raven y las joyas de la receta.',
      'Habla con el Trainer.',
      'Elige la opción de resucitar espíritus (“Resurrect Spirit” o su traducción en el cliente).',
      'Coloca los materiales de la receta en la ventana.',
      'Revisa el coste y la probabilidad que muestra la ventana antes de combinar.',
      'Si la combinación tiene éxito, recibirás el Dark Raven.',
    ],
    prev: 'dark-horse',
  },
  {
    id: 'dinorant',
    group: 'mascotas',
    machine: 'Chaos Machine',
    title: 'Crear Dinorant',
    shortTitle: 'Dinorant',
    aliases: ['dinorant', 'horn of dinorant', 'crear dinorant', 'uniria', 'horn of uniria', 'montura voladora'],
    summary: 'Montura voladora a partir de cuernos de Uniria.',
    npc: 'chaos-goblin',
    source: 'Chaos Machine (referencia clásica Season 6)',
    ingredients: [n('uniria', { gameName: 'Horn of Uniria', esName: 'Cuerno de Uniria' }, '10'), n('joc', JOC, '1')],
    results: [{ gameName: 'Horn of Dinorant', altNames: ['Dinorant'] }],
    zen: '500.000 Zen (referencia clásica)',
    zenStatus: 'clasica',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
    steps: ['Reúne 10 Horn of Uniria y una Jewel of Chaos.', 'Habla con el Chaos Goblin y abre la Chaos Machine.', 'Coloca los materiales y revisa el Zen y la probabilidad que muestra la ventana.', 'Combina.'],
  },
  {
    id: 'fenrir-fragmento',
    group: 'mascotas',
    machine: 'Chaos Machine',
    title: 'Fenrir · paso 1: Fragment of Horn',
    shortTitle: 'Fragment of Horn',
    aliases: ['fragment of horn', 'fragmento de cuerno', 'fenrir paso 1', 'splinter of armor', 'bless of guardian'],
    summary: 'Primer paso del Fenrir.',
    npc: 'chaos-goblin',
    source: 'Chaos Machine (referencia clásica Season 6)',
    ingredients: [n('splinter', { gameName: 'Splinter of Armor', esName: 'Astilla de armadura' }, '20'), n('bless-guardian', { gameName: 'Bless of Guardian', esName: 'Bendición del guardián' }, '20'), n('joc', JOC, '1')],
    results: [{ gameName: 'Fragment of Horn' }],
    zen: ZEN_PENDING,
    zenStatus: 'pendiente',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
    next: 'fenrir-cuerno-roto',
  },
  {
    id: 'fenrir-cuerno-roto',
    group: 'mascotas',
    machine: 'Chaos Machine',
    title: 'Fenrir · paso 2: Broken Horn',
    shortTitle: 'Broken Horn',
    aliases: ['broken horn', 'cuerno roto', 'fenrir paso 2', 'claw of beast'],
    summary: 'Segundo paso del Fenrir.',
    npc: 'chaos-goblin',
    source: 'Chaos Machine (referencia clásica Season 6)',
    ingredients: [
      n('claw', { gameName: 'Claw of Beast', esName: 'Garra de bestia' }, '5'),
      { ...n('fragment', { gameName: 'Fragment of Horn', esName: 'Fragmento de cuerno' }, '1'), recipeId: 'fenrir-fragmento' },
      n('joc', JOC, '1'),
    ],
    results: [{ gameName: 'Broken Horn' }],
    zen: ZEN_PENDING,
    zenStatus: 'pendiente',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
    prev: 'fenrir-fragmento',
    next: 'fenrir',
  },
  {
    id: 'fenrir',
    group: 'mascotas',
    machine: 'Chaos Machine',
    title: 'Fenrir · paso 3: Horn of Fenrir',
    shortTitle: 'Horn of Fenrir',
    aliases: ['fenrir', 'horn of fenrir', 'crear fenrir', 'lobo', 'fenrir paso 3'],
    summary: 'Último paso: la montura Fenrir.',
    npc: 'chaos-goblin',
    source: 'Chaos Machine (referencia clásica Season 6)',
    ingredients: [{ ...n('broken', { gameName: 'Broken Horn', esName: 'Cuerno roto' }, '1'), recipeId: 'fenrir-cuerno-roto' }, n('life', LIFE, '3'), n('joc', JOC, '1')],
    results: [{ gameName: 'Horn of Fenrir', altNames: ['Fenrir'] }],
    zen: ZEN_PENDING,
    zenStatus: 'pendiente',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
    prev: 'fenrir-cuerno-roto',
  },

  // ── Entradas de eventos ───────────────────────────────────────────────
  {
    id: 'entrada-devil-square',
    group: 'eventos',
    machine: 'Chaos Machine',
    title: 'Crear la entrada a Devil Square',
    shortTitle: 'Entrada a Devil Square',
    aliases: ['devil square', 'entrada devil square', 'invitation to devil square', 'ticket devil square', 'devils eye', 'devils key'],
    summary: 'Ojo y llave del diablo del mismo nivel.',
    npc: 'chaos-goblin',
    source: 'Chaos Machine (referencia clásica Season 6)',
    ingredients: [
      { ...n('eye', { gameName: "Devil's Eye", altNames: ['Devils Eye'], esName: 'Ojo del diablo' }, '1'), requirement: 'Mismo nivel que la llave', requirementStatus: 'clasica' },
      { ...n('key', { gameName: "Devil's Key", altNames: ['Devils Key'], esName: 'Llave del diablo' }, '1'), requirement: 'Mismo nivel que el ojo', requirementStatus: 'clasica' },
      n('joc', JOC, '1'),
    ],
    results: [{ gameName: 'Invitation to Devil Square', altNames: ["Devil's Invitation"] }],
    resultNote: 'El nivel de la entrada depende del nivel de los ingredientes (referencia clásica).',
    zen: ZEN_PENDING,
    zenStatus: 'pendiente',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
  },
  {
    id: 'entrada-blood-castle',
    group: 'eventos',
    machine: 'Chaos Machine',
    title: 'Crear la entrada a Blood Castle',
    shortTitle: 'Entrada a Blood Castle',
    aliases: ['blood castle', 'entrada blood castle', 'cloak of invisibility', 'capa de invisibilidad', 'scroll of archangel', 'blood bone'],
    summary: 'Pergamino del arcángel y hueso de sangre del mismo nivel.',
    npc: 'chaos-goblin',
    source: 'Chaos Machine (referencia clásica Season 6)',
    ingredients: [
      { ...n('archangel', { gameName: 'Scroll of Archangel', esName: 'Pergamino del arcángel' }, '1'), requirement: 'Mismo nivel que el hueso', requirementStatus: 'clasica' },
      { ...n('bone', { gameName: 'Blood Bone', esName: 'Hueso de sangre' }, '1'), requirement: 'Mismo nivel que el pergamino', requirementStatus: 'clasica' },
      n('joc', JOC, '1'),
    ],
    results: [{ gameName: 'Cloak of Invisibility', altNames: ['Invisibility Cloak'] }],
    resultNote: 'El nivel de la entrada depende del nivel de los ingredientes (referencia clásica).',
    zen: ZEN_PENDING,
    zenStatus: 'pendiente',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
  },
  {
    id: 'entrada-illusion-temple',
    group: 'eventos',
    machine: 'Chaos Machine',
    title: 'Crear la entrada a Illusion Temple',
    shortTitle: 'Entrada a Illusion Temple',
    aliases: ['illusion temple', 'entrada illusion temple', 'scroll of blood', 'old scroll', 'covenant of illusion sorcerer'],
    summary: 'Pergamino antiguo y pacto del hechicero del mismo nivel.',
    npc: 'chaos-goblin',
    source: 'Chaos Machine (referencia clásica Season 6)',
    ingredients: [
      { ...n('old-scroll', { gameName: 'Old Scroll', esName: 'Pergamino antiguo' }, '1'), requirement: 'Mismo nivel que el pacto', requirementStatus: 'clasica' },
      { ...n('covenant', { gameName: 'Illusion Sorcerer Covenant', altNames: ['Covenant of Illusion Sorcerer'], esName: 'Pacto del hechicero de la ilusión' }, '1'), requirement: 'Mismo nivel que el pergamino', requirementStatus: 'clasica' },
      n('joc', JOC, '1'),
    ],
    results: [{ gameName: 'Scroll of Blood' }],
    resultNote: 'El nivel de la entrada depende del nivel de los ingredientes (referencia clásica).',
    zen: ZEN_PENDING,
    zenStatus: 'pendiente',
    failure: FAIL_PENDING,
    failureStatus: 'pendiente',
  },
]

/** Ruta de la receta en la guía: alas y capas en /guia/alas, el resto en /guia/combinaciones. */
export const recipePath = (id: string) => {
  const r = recipes.find((x) => x.id === id)
  return !r || !r.group || r.group === 'alas' ? `/guia/alas/${id}` : `/guia/combinaciones/${id}`
}

export const recipeGroups: { id: 'mascotas' | 'eventos'; title: string; description: string }[] = [
  { id: 'mascotas', title: 'Mascotas y monturas', description: 'Dark Horse y Dark Raven con el Trainer; Dinorant y Fenrir en la Chaos Machine.' },
  { id: 'eventos', title: 'Entradas de eventos', description: 'Entradas de Devil Square, Blood Castle e Illusion Temple.' },
]

export const getRecipe = (id: string | undefined) => recipes.find((r) => r.id === id)

/** Todos los resultados e ingredientes de una receta, incluidas sus variantes por clase. */
export const recipeResults = (r: Recipe) => [...r.results, ...(r.variants ?? []).flatMap((v) => v.results)]
export const recipeIngredients = (r: Recipe) => [...r.ingredients, ...(r.variants ?? []).flatMap((v) => v.ingredients)]

/** Orden de las opciones al entrar a la guía de alas. */
export const recipeMenu = ['arma-chaos', 'alas-evo-1', 'alas-evo-2', 'capas', 'pluma-condor', 'alas-evo-3']
